import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import { ENTITLEMENT_DETAILS_TABS } from '../../../../../../../shared/constants';
import { useTypedSelector } from '../../../../../../../shared/hooks';
import { detailsActions } from '../../../../../../../shared/store/actions';
import { type RiskItem } from '../../../InherentRisksTab/data-layer';
import { useSearchEntitlementConflicts } from '../hooks';

import RiskConflictsDisplay from './ConflictsDisplay/ConflictsDisplay';
import RiskConflictsSP from './ConflictsSidepanel/ConflictsSP';

interface RiskDetailsConflictsTabProps {
  activeTab?: string;
}

/**
 * RiskDetailsConflictsTab Component
 *
 * Entitlement risk details conflicts tab that integrates with the new search
 * violating entitlements API. Displays violation conflicts using selected
 * risk data and provides side panel support with responsive layout.
 *
 * @component
 * @returns {React.ReactElement} Risk conflicts tab with API integration
 *
 * @example
 * <RiskDetailsConflictsTab
 *   activeTab={ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS}
 * />
 */
function RiskDetailsConflictsTab({
  activeTab = ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS,
}: RiskDetailsConflictsTabProps): ReactElement {
  const dispatch = useDispatch();
  const {
    searchEntitlementConflicts,
    loadMore,
    data,
    functionGroupedData,
    clearData,
    isLoading,
    hasMore,
    functionCounts,
  } = useSearchEntitlementConflicts();

  const {
    entitlementSummary: { selectedEntitlement },
    entitlementRisks: { selectedEntitlementRisk, list },
    entitlementConflicts: {
      selectedRowData,
      selectedRisk: conflictsRisk,
      sidepanel: { isSidePanelVisible },
      controls: { activeFilters, searchKey },
    },
  } = useTypedSelector((state) => state.aag.details);

  // Check if all function APIs are loaded with data
  const isAllFunctionsLoaded = useMemo(() => {
    const risk =
      conflictsRisk ??
      selectedEntitlementRisk ??
      (list.data[0] as RiskItem | undefined);

    if (!risk) {
      return true; // No risk means no functions to load
    }

    const functionKeys = [
      risk.function1Key,
      risk.function2Key,
      risk.function3Key,
      risk.function4Key,
      risk.function5Key,
    ].filter(Boolean);

    if (functionKeys.length === 0) {
      return true; // No functions means all are "loaded"
    }

    return functionKeys.every((key) => {
      const functionData = functionGroupedData[String(key)];

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return functionData !== undefined;
    });
  }, [conflictsRisk, selectedEntitlementRisk, list.data, functionGroupedData]);

  const shouldShowLoader = isLoading || !isAllFunctionsLoaded;

  const getBaseParams = useCallback(() => {
    const filterPayload = activeFilters.reduce<Record<string, string[]>>(
      (acc, filter) => {
        const filterValues = filter.options.map((option) => option.value);

        switch (filter.value) {
          case 'violatingSoxCriticality':
            acc.violatingSoxCriticality = filterValues;
            break;
          case 'violatingSysCriticality':
            acc.violatingSysCriticality = filterValues;
            break;
          case 'violatingPrivileged':
            acc.violatingPrivileged = filterValues;
            break;
          case 'violatingEntitlements':
            acc.violatingEntitlements = filterValues;
            break;
        }

        return acc;
      },
      {}
    );

    const risk =
      conflictsRisk ??
      selectedEntitlementRisk ??
      (list.data[0] as RiskItem | undefined);

    if (!selectedEntitlement?.entitlementValueKey || !risk) {
      return null;
    }

    return {
      riskKey: risk.riskId.toString(),
      assignedEntitlementId: selectedEntitlement.entitlementValueKey.toString(),
      payload: {
        ...filterPayload,
        globalSearch: searchKey ?? '',
        max: 50,
      },
    };
  }, [
    activeFilters,
    conflictsRisk,
    selectedEntitlementRisk,
    list.data,
    selectedEntitlement,
    searchKey,
  ]);

  const handleSearchConflicts = useCallback(async () => {
    try {
      const params = getBaseParams();
      const risk =
        conflictsRisk ??
        selectedEntitlementRisk ??
        (list.data[0] as RiskItem | undefined);

      if (params) {
        // Call API for all available functions
        const functionKeys = [
          risk?.function1Key,
          risk?.function2Key,
          risk?.function3Key,
          risk?.function4Key,
          risk?.function5Key,
        ].filter(Boolean);

        await Promise.all(
          functionKeys.map((functionKey) =>
            searchEntitlementConflicts({
              ...params,
              functionId: String(functionKey),
              payload: {
                ...params.payload,
                searchAfter: undefined,
              },
            })
          )
        );
      }
    } catch (error) {
      // handle error catch
    }
  }, [
    getBaseParams,
    searchEntitlementConflicts,
    conflictsRisk,
    selectedEntitlementRisk,
    list.data,
  ]);

  const handleLoadMore = useCallback(async (): Promise<void> => {
    try {
      const params = getBaseParams();
      const risk =
        conflictsRisk ??
        selectedEntitlementRisk ??
        (list.data[0] as RiskItem | undefined);

      if (params && risk?.function1Key) {
        await loadMore({
          ...params,
          functionId: String(risk.function1Key),
        });
      }
    } catch (error) {
      // handle error catch
    }
  }, [
    getBaseParams,
    loadMore,
    conflictsRisk,
    selectedEntitlementRisk,
    list.data,
  ]);

  useEffect(() => {
    if (
      activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS &&
      selectedEntitlement?.entitlementValueKey
    ) {
      if (!conflictsRisk && list.data.length > 0) {
        const riskToUse: RiskItem | null =
          selectedEntitlementRisk ??
          (list.data[0] as RiskItem | undefined) ??
          null;

        dispatch(detailsActions.setEntitlementConflictsSelectedRisk(riskToUse));
      }
    }
  }, [
    activeTab,
    conflictsRisk,
    selectedEntitlementRisk,
    list.data,
    selectedEntitlement?.entitlementValueKey,
    dispatch,
  ]);

  // Trigger search when filters or search key changes
  useEffect(() => {
    if (
      activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS &&
      conflictsRisk &&
      selectedEntitlement?.entitlementValueKey &&
      !isLoading
    ) {
      handleSearchConflicts().catch(() => {
        // Handle error silently
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeFilters,
    searchKey,
    activeTab,
    conflictsRisk?.riskId,
    selectedEntitlement?.entitlementValueKey,
  ]);

  // Update conflictsRisk when selectedEntitlementRisk changes and clear filters
  useEffect(() => {
    if (selectedEntitlementRisk && selectedEntitlementRisk !== conflictsRisk) {
      // Clear search, filters, and data first
      dispatch(detailsActions.setEntitlementConflictsSearchKey(''));
      dispatch(detailsActions.setEntitlementConflictsActiveFilters([]));
      clearData();

      dispatch(
        detailsActions.setEntitlementConflictsSelectedRisk(
          selectedEntitlementRisk
        )
      );

      // Trigger initial search when risk changes with cleared filters
      if (
        activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS &&
        selectedEntitlement?.entitlementValueKey &&
        !isLoading
      ) {
        const params = {
          riskKey: selectedEntitlementRisk.riskId.toString(),
          assignedEntitlementId:
            selectedEntitlement.entitlementValueKey.toString(),
          payload: {
            globalSearch: '',
            max: 50,
            searchAfter: undefined,
          },
        };

        const functionKeys = [
          selectedEntitlementRisk.function1Key,
          selectedEntitlementRisk.function2Key,
          selectedEntitlementRisk.function3Key,
          selectedEntitlementRisk.function4Key,
          selectedEntitlementRisk.function5Key,
        ].filter(Boolean);

        Promise.all(
          functionKeys.map((functionKey) =>
            searchEntitlementConflicts({
              ...params,
              functionId: String(functionKey),
            })
          )
        ).catch(() => {
          // Handle error silently
        });
      }
    }
  }, [
    selectedEntitlementRisk,
    conflictsRisk,
    dispatch,
    clearData,
    activeTab,
    selectedEntitlement?.entitlementValueKey,
    isLoading,
    searchEntitlementConflicts,
  ]);

  useEffect(() => {
    if (isLoading) {
      dispatch(detailsActions.setEntitlementConflictsSidePanelClose());
    }
  }, [isLoading, dispatch]);

  const wrapperClasses = cx(
    'Sod-EntitlementTabDetails-ConflictsTab Sod-ViolationsTabDetails',
    isSidePanelVisible &&
      selectedRowData &&
      'Sod-ViolationsTabDetails--isSidePanelVisible Sod-Details-commonMainWrapper'
  );
  const contentClasses = cx(
    'Sod-ViolationsTabDetails-content',
    isSidePanelVisible &&
      selectedRowData &&
      'Sod-ViolationsTabDetails-content--isSidePanelVisible'
  );
  const asidesClasses = cx('Sod-ViolationsTabDetails-asides');

  return (
    <Box tag='div' className={wrapperClasses}>
      <Box tag='div' className={contentClasses}>
        <RiskConflictsDisplay
          activeTab={activeTab}
          data={data?.data ?? []}
          functionGroupedData={functionGroupedData}
          className='Sod-ViolationsTabDetails-tabContent'
          isLoading={shouldShowLoader}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          functionCount={functionCounts}
        />
      </Box>
      <Box tag='div' className={asidesClasses}>
        {selectedRowData && <RiskConflictsSP />}
      </Box>
    </Box>
  );
}

RiskDetailsConflictsTab.defaultProps = {
  activeTab: ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS,
};

export default RiskDetailsConflictsTab;
